import React from 'react';
import { Button } from '@/components/ui/button';
import { FileText, Trophy, Eye, Download, Calculator, Archive, ArchiveRestore } from 'lucide-react';

const CommitteeApplicantsTab = ({
  applications,
  showArchived,
  setShowArchived,
  getStatusBadge,
  getScoreSummaryLabel,
  viewApplicantProfile,
  downloadApplicationPdf,
  downloadingPdf,
  openScoringDialog,
  setAwardDialog,
  setArchiveDialog,
  unarchiveApplication,
  isChairOrCoChair
}) => {
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Applicant Management</h3>
          <p className="text-sm text-slate-500">Review, award, and archive scholarship applications</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show Archived
          </label>
        </div>
      </div>
      
      {applications.length === 0 ? (
        <div className="p-8 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No applications yet</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-200">
          {applications.map((app) => (
            <div
              key={app.id}
              className={`p-4 ${app.is_archived ? 'bg-slate-50' : ''}`}
              data-testid={`application-row-${app.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-slate-900">{app.applicant_name}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadge(app.status)}`}>
                      {app.status?.replace('_', ' ').charAt(0).toUpperCase() + app.status?.slice(1).replace('_', ' ')}
                    </span>
                    {app.is_previous_recipient && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 flex items-center gap-1">
                        <Trophy className="w-3 h-3" />
                        Previous Recipient
                      </span>
                    )}
                    {app.is_archived && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-slate-200 text-slate-500">
                        Archived
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{app.applicant_email}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Scholarship: <span className="font-medium">{app.scholarship_name}</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Submitted: {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Not submitted'}
                  </p>
                  {getScoreSummaryLabel(app) && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid={`application-score-summary-${app.id}`}>
                      <span className="rounded-full bg-blue-100 px-2 py-1 font-medium text-blue-800">
                        Avg Score: {getScoreSummaryLabel(app)}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">
                        {app.score_summary?.completed_reviewers || 0} completed review{app.score_summary?.completed_reviewers === 1 ? '' : 's'}
                      </span>
                      {(app.score_summary?.draft_reviewers || 0) > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-700">
                          {app.score_summary?.draft_reviewers} draft review{app.score_summary?.draft_reviewers === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  )}
                  {app.previous_awards_count > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Has received {app.previous_awards_count} scholarship(s) previously
                    </p>
                  )}
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => viewApplicantProfile(app.applicant_id)}
                    disabled={app.applicant_name === 'Unknown Applicant'}
                    data-testid={`view-profile-${app.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Profile
                  </Button>
                  
                  {/* PDF Download Button */}
                  {['submitted', 'under_review'].includes(app.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadApplicationPdf(app.id, app.applicant_name)}
                      disabled={downloadingPdf === app.id}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid={`download-pdf-${app.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {downloadingPdf === app.id ? 'Downloading...' : 'PDF'}
                    </Button>
                  )}
                  
                  {/* Score Button - visible to all committee members for submitted apps */}
                  {['submitted', 'under_review'].includes(app.status) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openScoringDialog(app)}
                      className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      data-testid={`score-btn-${app.id}`}
                    >
                      <Calculator className="w-4 h-4 mr-1" />
                      {getScoreSummaryLabel(app) ? 'Update Score' : 'Score'}
                    </Button>
                  )}
                  
                  {!app.is_archived && ['submitted', 'under_review'].includes(app.status) && isChairOrCoChair() && (
                    <Button
                      size="sm"
                      onClick={() => setAwardDialog({ open: true, application: app })}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`award-btn-${app.id}`}
                    >
                      <Trophy className="w-4 h-4 mr-1" />
                      Award
                    </Button>
                  )}
                  
                  {!app.is_archived ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setArchiveDialog({ open: true, application: app })}
                      className="text-slate-600 border-slate-300"
                      data-testid={`archive-btn-${app.id}`}
                    >
                      <Archive className="w-4 h-4 mr-1" />
                      Archive
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => unarchiveApplication(app.id)}
                      className="text-blue-600 border-blue-300"
                      data-testid={`unarchive-btn-${app.id}`}
                    >
                      <ArchiveRestore className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommitteeApplicantsTab;